import { jsx } from '@amazon/vinyl-tsx'
import { data } from '@amazon/vinyl-observable'
import {
    type RecommendationModel,
    type RecommendationRendererDeps,
    toRecommendationModel,
} from '@/browse/RecommendationRenderer'
import { RecommendationsShoveler } from '@/browse/RecommendationsShoveler'
import { ImageSizes } from '@/style/styleCommon'
import { LoadingController, LoadingIndicator } from '@/components/LoadingIndicator'
import { ctx } from '@/util/context'
import { fetchLibraryHome, type LibraryHomeResult } from '@/library/libraryService'
import type { EntityUnion } from '@/service/fragments/entity'
import { Icon } from '@/components/Icon'
import { CreatePlaylistModal } from '@/library/CreatePlaylistModal'
import { LibraryChipNav } from '@/library/LibraryChipNav'
import { NewPlaylistMenu } from '@/library/NewPlaylistMenu'
import { getString } from '@/library/i18n/bundle.generated'
import { signedIn } from '@/auth/authRef'
import { SignedInRequired } from '@/components/SignedInRequired'

const { target, errorHandler } = ctx('library')
void target

export type LibraryDeps = RecommendationRendererDeps

export interface LibraryProps {
    readonly deps: LibraryDeps
}

interface ShovelerSpec {
    readonly id: string
    readonly title: string
    readonly nodes: readonly EntityUnion[]
}

/**
 * Library home — mirrors green hornet's `LibraryHome`. Renders four shovelers
 * from the authenticated user's data: owned playlists, Made For You, recently
 * played containers, and song history. Adds a "+ New Playlist" button in the
 * Your Playlists shoveler header that opens a popover with two choices —
 * Curate (create-by-name modal) or Create with AI (Maestro placeholder).
 */
export function Library(props: LibraryProps) {
    const loader = new LoadingController()
    // Modal + popover state is owned by the page so the button-spawned
    // popover can close itself before opening the modal.
    const createModalVisible = data(false)
    const newPlaylistMenuVisible = data(false)
    const collections = (<div className="musicCollections" />) as HTMLDivElement
    // Library is gated to signed-in users — without auth all the
    // shoveler queries would fail with UnauthorizedError. Render a
    // friendly empty state and skip the fetch entirely.
    const view = (
        <div className="libraryHome browseHome">
            <SignedInRequired />
            <div visible={signedIn()}>
                <LibraryChipNav />
                {collections}
                <LoadingIndicator visible={loader.loading} />
            </div>
            <CreatePlaylistModal visible={createModalVisible} />
        </div>
    ) as HTMLDivElement

    function openCurate() {
        newPlaylistMenuVisible.value = false
        createModalVisible.value = true
    }

    // Skip the gateway round-trip + shoveler render when signed
    // out — the `<SignedInRequired>` empty state is the entire
    // surface, and every shoveler query requires `isAuthenticated`.
    // Re-runs whenever auth flips so a sign-in mid-session lights
    // the page up without a reload.
    signedIn().onData((authed) => {
        if (!authed) return
        fetchLibraryHome()
            .then((result) => {
                const specs = buildShovelerSpecs(result)
                for (const spec of specs) {
                    const items = spec.nodes
                        .map((node) => toRecommendationModel(node))
                        .filter((m): m is RecommendationModel => m != null)
                    if (items.length === 0) continue
                    const headerExtras =
                        spec.id === 'user-playlists' ? (
                            <div className="rowHeaderActions">
                                <button
                                    className="textButton newPlaylistBtn"
                                    onclick={(event) => {
                                        event.stopPropagation()
                                        newPlaylistMenuVisible.value = !newPlaylistMenuVisible.value
                                    }}
                                >
                                    <Icon icon="add" />
                                    <span className="label">
                                        {/* Hornet's localized value is "+ New
                                        Playlist" — strip the leading +
                                        + whitespace at render time so we
                                        don't double up with the icon. The
                                        underlying loc string is
                                        intentionally left untouched. */}
                                        {getString(
                                            'dmusic_library_playlists_create_playlist_button_text'
                                        ).map((s) => s.replace(/^\+\s*/, ''))}
                                    </span>
                                </button>
                                <NewPlaylistMenu
                                    visible={newPlaylistMenuVisible}
                                    onCurate={openCurate}
                                />
                            </div>
                        ) : null
                    collections.appendChild(
                        renderShoveler(spec.title, items, props.deps, headerExtras)
                    )
                }
            })
            .catch(errorHandler)
            .finally(loader.show())
    })

    return view
}

function buildShovelerSpecs(result: LibraryHomeResult): readonly ShovelerSpec[] {
    const user = result.user
    const playlistEdges = user?.playlists?.edges ?? []
    const followedEdges = user?.followedPlaylists?.edges ?? []
    const recentEntityEdges = user?.recentEntitiesPlayback?.edges ?? []
    const recentTrackEdges = user?.recentTrackPlayback?.edges ?? []

    return [
        {
            id: 'user-playlists',
            title: 'Your Playlists',
            nodes: playlistEdges.map((e) => e.node as EntityUnion),
        },
        {
            id: 'made-for-you',
            title: 'Made For You',
            nodes: followedEdges.map((e) => e.node as EntityUnion),
        },
        {
            id: 'recently-played',
            title: 'Recently Played',
            nodes: recentEntityEdges.map((e) => e.node as EntityUnion),
        },
        {
            id: 'song-history',
            title: 'Song History',
            nodes: recentTrackEdges.map((e) => e.node as EntityUnion),
        },
    ]
}

function renderShoveler(
    title: string,
    items: readonly RecommendationModel[],
    deps: RecommendationRendererDeps,
    headerExtras: HTMLElement | null
): HTMLElement {
    return (
        <div className="recommendationContainer">
            <RecommendationsShoveler
                className="large recommendations"
                headerTitle={title}
                header={
                    <div className="rowHeader">
                        <h2>{title}</h2>
                        {headerExtras}
                    </div>
                }
                imageSize={ImageSizes.LARGE}
                data={items}
                deps={deps}
            />
        </div>
    )
}
